
// Calculation logic for Japanese Holidays
// This avoids external API dependencies and CORS issues with Cabinet Office CSV

interface Holiday {
  date: string; // YYYY-MM-DD
  name: string;
}

const WEEKDAYS = {
  SUN: 0,
  MON: 1,
};

// Vernal Equinox Day calculation (valid until 2099)
const getVernalEquinoxDay = (year: number): number => {
  return Math.floor(20.8431 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
};

// Autumnal Equinox Day calculation (valid until 2099)
const getAutumnalEquinoxDay = (year: number): number => {
  return Math.floor(23.2488 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
};

// Helper to format date
const formatDate = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Helper to get Nth Monday of the month
const getNthMonday = (year: number, month: number, n: number): number => {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const firstMonday = firstDay === 1 ? 1 : 1 + (7 - firstDay + 1) % 7;
  return firstMonday + (n - 1) * 7;
};

export const getHolidaysForYear = (year: number): Record<string, string> => {
  const holidays: Record<string, string> = {};

  const add = (month: number, day: number, name: string) => {
    holidays[formatDate(year, month, day)] = name;
  };

  // --- Fixed Holidays ---
  add(1, 1, '元日');
  add(2, 11, '建国記念の日');
  add(2, 23, '天皇誕生日');
  add(4, 29, '昭和の日');
  add(5, 3, '憲法記念日');
  add(5, 4, 'みどりの日');
  add(5, 5, 'こどもの日');
  add(8, 11, '山の日');
  add(11, 3, '文化の日');
  add(11, 23, '勤労感謝の日');

  // --- Happy Mondays ---
  add(1, getNthMonday(year, 1, 2), '成人の日');
  add(7, getNthMonday(year, 7, 3), '海の日');
  add(9, getNthMonday(year, 9, 3), '敬老の日');
  add(10, getNthMonday(year, 10, 2), 'スポーツの日');

  // --- Equinoxes ---
  add(3, getVernalEquinoxDay(year), '春分の日');
  add(9, getAutumnalEquinoxDay(year), '秋分の日');

  // --- Substitute Holidays (Furikae Kyujitsu) ---
  // If a holiday falls on Sunday, the next weekday is a holiday
  // Sort dates to process sequentially
  const sortedDates = Object.keys(holidays).sort();
  
  // We need to track created substitute holidays to avoid chaining incorrectly if needed,
  // though standard rules are simple: search next available day.
  // Note: Standard rule is "next day that is not a holiday".
  
  // Use a temporary map to store original holidays to check against
  const originalHolidays = { ...holidays };

  for (const dateStr of sortedDates) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    
    if (dateObj.getDay() === WEEKDAYS.SUN) {
      let nextDay = new Date(y, m - 1, d + 1);
      while (originalHolidays[formatDate(nextDay.getFullYear(), nextDay.getMonth() + 1, nextDay.getDate())]) {
        nextDay.setDate(nextDay.getDate() + 1);
      }
      holidays[formatDate(nextDay.getFullYear(), nextDay.getMonth() + 1, nextDay.getDate())] = '振替休日';
    }
  }

  // --- Citizens' Holidays (Kokumin no Kyujitsu) ---
  // Any day that falls between two other holidays is a holiday (unless it's already a holiday)
  // Usually happens in September (Silver Week) or May (Golden Week - though May 4 is now fixed)
  
  // Re-sort including substitutes
  const allHolidayDates = Object.keys(holidays).sort();
  
  for (let i = 0; i < allHolidayDates.length - 1; i++) {
    const current = new Date(allHolidayDates[i]);
    const next = new Date(allHolidayDates[i+1]);
    
    // Check if there is exactly one day gap
    const diffTime = Math.abs(next.getTime() - current.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    
    if (diffDays === 2) {
      // The day in between
      const sandwichDay = new Date(current);
      sandwichDay.setDate(current.getDate() + 1);
      const sandwichStr = formatDate(sandwichDay.getFullYear(), sandwichDay.getMonth() + 1, sandwichDay.getDate());
      
      // If not already a holiday (and not Sunday/Saturday logic which usually doesn't apply to Kokumin definition directly but good to check)
      if (!holidays[sandwichStr]) {
         holidays[sandwichStr] = '国民の休日';
      }
    }
  }

  return holidays;
};
